import type {
  FormFieldResponse,
  InspectionDeviceFieldValueRequest,
  InspectionDeviceFieldValueResponse,
} from 'src/api/ProactApiClient';
import type {SubmissionEntry} from 'src/app/forms/engine/useProactForm';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';
import {mapFormFieldSimpleResponseToFormFieldV2} from 'src/types/FormV2Mapping';

// Engine keys fields by InvariantId; backend keys by int FormFieldId. This is
// the only boundary that translates between them.

type MeasurementValue = {magnitude?: number | string | ''; unit?: string};

export const mapInspectionDeviceFieldsToFormFieldsV2 = (
  fields: FormFieldResponse[]
): IFormFieldV2[] =>
  [...fields]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map(field =>
      mapFormFieldSimpleResponseToFormFieldV2({
        ...field,
        subFormFieldId: undefined,
        subFormFieldInvariantId: undefined,
      })
    );

/** Seeds engine form values from the entry payload's `values` (keyed by InvariantId). */
export const buildDeviceEntryFormData = (
  formMeta: IFormFieldV2[],
  values: InspectionDeviceFieldValueResponse[]
): IFormDataV2 => {
  const byInvariantId = new Map(
    values.map(value => [value.formFieldInvariantId, value])
  );
  const data: IFormDataV2 = {};

  formMeta.forEach(field => {
    const value = byInvariantId.get(field.invariantId ?? field.name);
    if (!value) return;

    if (field.type === 'measurement') {
      data[field.name] = {
        magnitude: value.value ?? '',
        unit: value.unit ?? '',
      } as MeasurementValue;
    } else {
      data[field.name] = value.value ?? '';
    }
  });

  return data;
};

const isEmpty = (value: unknown): boolean =>
  value === undefined || value === null || value === '';

/** Maps engine submission entries back to `InspectionDeviceFieldValueRequest[]`. */
export const buildDeviceRecordValues = (
  entries: SubmissionEntry[]
): InspectionDeviceFieldValueRequest[] =>
  entries
    .filter(entry => entry.field.builderOptions?.id !== undefined)
    .map(entry => {
      const formFieldId = entry.field.builderOptions!.id as number;

      if (entry.field.type === 'measurement') {
        const measurement = (entry.value ?? {}) as MeasurementValue;
        return {
          formFieldId,
          value: isEmpty(measurement.magnitude)
            ? undefined
            : String(measurement.magnitude),
          unit: measurement.unit || undefined,
        };
      }

      return {
        formFieldId,
        value: isEmpty(entry.value) ? undefined : String(entry.value),
      };
    });

interface NormalizedApiErrorLike {
  status?: number;
  response?: unknown;
}

/** `POST/PUT .../devices/{deviceId}` 409 with `code: 'stale-field-version'`. */
export const isStaleFieldVersionError = (error: unknown): boolean => {
  const err = error as NormalizedApiErrorLike | undefined;
  if (!err || err.status !== 409) return false;

  try {
    const body =
      typeof err.response === 'string'
        ? JSON.parse(err.response)
        : err.response;
    return (
      (body as {code?: string} | null | undefined)?.code ===
      'stale-field-version'
    );
  } catch {
    return false;
  }
};
